import React, { useContext, useEffect, useState } from "react";
import { SidebarProps } from "react-sidebar";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IXeroCreateInvoiceDTO from "../../../models/DTO/IXeroCreateInvoiceDTO";
import IXeroDeleteInvoiceDTO from "../../../models/DTO/IXeroDeleteInvoiceDTO";
import IXeroGetInvoicePdfDTO from "../../../models/DTO/IXeroGetInvoicePdfDTO";
import IXeroOnCreateInvoiceDTO from "../../../models/DTO/IXeroOnCreateInvoiceDTO";
import IXeroOnGetInvoicePdfDTO from "../../../models/DTO/IXeroOnGetInvoicePdfDTO";
import IXeroVoidInvoiceDTO from "../../../models/DTO/IXeroVoidInvoiceDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOrder from "../../../models/IOrder";
import HelperService from "../../../services/HelperService";
import { OrderXeroInvoiceSetup } from "../../shared/OrderXeroInvoiceSetup/OrderXeroInvoiceSetup";
import OwButton from "../../shared/OwButton/OwButton";
import OwTranslate from "../../shared/OwTranslate/OwTranslate";
import { XeroInvoiceSidebar } from "../../shared/XeroInvoiceSidebar/XeroInvoiceSidebar";
import { Socket } from "socket.io-client";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";

type Props = {
    orderModel: IOrder;
};

export const OrderXeroInvoice: React.FC<Props> = (props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [isActive, setIsActive] = useState(false);

    const onViewInvoice = (data: IXeroOnGetInvoicePdfDTO) => {
        // const ab = new Uint8Array(data.data).buffer;

        const sidebar = (
            <XeroInvoiceSidebar
                file={data.data}
                toggleSidebar={uiContext.toggleSidebar}
                orderModel={props.orderModel}
                socket={socket}
                notifyFn={uiContext.notifyFn}
            />
        );

        uiContext.setSidebarComponent(sidebar);
        uiContext.toggleSidebar(true);

        setIsActive(false);
    };

    const onCreateInvoice = (data: IXeroOnCreateInvoiceDTO) => {
        setIsActive(false);
    };

    const onVoidInvoice = () => {
        setIsActive(false);
    };

    const notify = () => {
        setIsActive(false);
    };

    const onDeleteInvoice = () => {
        setIsActive(false);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.XeroOnCreateInvoice, fn: onCreateInvoice },
        { endpoint: EndpointsEnum.Notify, fn: notify },
        { endpoint: EndpointsEnum.XeroOnGetInvoicePdf, fn: onViewInvoice },
        { endpoint: EndpointsEnum.XeroOnVoidInvoice, fn: onVoidInvoice },
        { endpoint: EndpointsEnum.XeroOnDeleteInvoice, fn: onDeleteInvoice },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const viewInvoice = () => {
        const payload: IXeroGetInvoicePdfDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: props.orderModel.guid,
        };

        socket.emit(EndpointsEnum.XeroGetInvoicePdf, payload);

        setIsActive(true);
    };

    const showCreateInvoiceSidebar = () => {
        const component = (
            <OrderXeroInvoiceSetup createFn={createInvoice} cancelFn={cancelCreateInvoice} socket={socket} notifyFn={uiContext.notifyFn} />
        );

        uiContext.setSidebarComponent(component);
        uiContext.toggleSidebar(true);
    };

    const cancelCreateInvoice = () => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);
    };

    const createInvoice = (initialData: Partial<IXeroCreateInvoiceDTO>) => {
        const payload: IXeroCreateInvoiceDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: props.orderModel.guid,
            shippingCost: 0,
            dueDate: initialData.dueDate!,
        };

        socket.emit(EndpointsEnum.XeroCreateInvoice, payload);

        setIsActive(true);
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);
    };

    const voidInvoice = () => {
        const payload: IXeroVoidInvoiceDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: props.orderModel.guid,
        };

        socket.emit(EndpointsEnum.XeroVoidInvoice, payload);

        setIsActive(true);
    };

    const deleteInvoice = () => {
        const payload: IXeroDeleteInvoiceDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            orderGuid: props.orderModel.guid,
        };

        socket.emit(EndpointsEnum.XeroDeleteInvoice, payload);

        setIsActive(true);
    };

    return (
        <div className={"OrderXeroInvoice"}>
            <fieldset className={"no-wrap "}>
                <legend>
                    <OwTranslate defaultMessage={"xero"} id={"xero"} />
                </legend>
                {props.orderModel.xero_invoice_id ? (
                    <div>
                        <OwButton
                            title={"view_invoice"}
                            fn={viewInvoice}
                            leftIconClass={"fas fa-plus"}
                            active={isActive}
                            disableOnActive={true}
                        />
                        <OwButton
                            className={"ms-2 btn-danger"}
                            title={"void_invoice"}
                            fn={voidInvoice}
                            leftIconClass={"fas fa-ban"}
                            active={isActive}
                            disableOnActive={true}
                        />
                        <OwButton
                            className={"ms-2 btn-danger"}
                            title={"delete_invoice"}
                            fn={deleteInvoice}
                            leftIconClass={"fas fa-trash"}
                            active={isActive}
                            disableOnActive={true}
                        />
                    </div>
                ) : (
                    <OwButton
                        title={"create_invoice"}
                        fn={showCreateInvoiceSidebar}
                        leftIconClass={"fas fa-plus"}
                        active={isActive}
                        disableOnActive={true}
                    />
                )}
            </fieldset>
        </div>
    );
};
